package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * The share of a file's existing keys that may be marked obsolete before the server demands an
 * explicit confirmation naming the count.
 *
 * Twenty per cent, and the number matters less than the fact that there is one. Below it, obsoletion
 * is ordinary housekeeping — a release removed a screen. Above it, something is usually wrong with
 * the *input* rather than the intent: a partial file set, the wrong locale, a glob that missed a
 * directory. The failure this exists for is an autonomous agent asked to "clean up unused strings",
 * which runs a push against whatever files it happened to find and marks most of a project obsolete
 * without anyone deciding anything.
 */
const val OBSOLETION_FORCE_THRESHOLD = 0.20

/**
 * The smallest obsoletion that can ever require confirmation, whatever the percentage.
 *
 * A percentage alone is the wrong instrument on a small file: removing one key from a four-key file
 * is 25%, and stopping to confirm it is friction with no safety in it — the whole change is visible
 * at a glance and one re-import undoes it. Worse, it is *habit-forming* friction. A guard that fires
 * on routine edits teaches people, and scripts, to pass the confirmation reflexively, and a
 * confirmation that is always passed is not a confirmation. Five keys is the floor because the
 * failure this guards against — a partial file set, a glob that missed a directory — never removes
 * two keys; it removes hundreds.
 */
const val MIN_OBSOLETION_FOR_FORCE = 5

/**
 * Whether this import must be explicitly forced.
 *
 * The denominator is the keys already in **the file being imported**, because obsoletion is scoped
 * to that file — measuring against the whole project would let a small file quietly empty itself.
 *
 * A file with no keys yet cannot destroy anything, so nothing is ever forced on a first import. This
 * is deliberate and not a hole: with no existing keys there is no work to lose.
 */
fun requiresObsoletionForce(willObsolete: Int, existingKeys: Int): Boolean =
    existingKeys > 0 &&
        willObsolete >= MIN_OBSOLETION_FOR_FORCE &&
        willObsolete.toDouble() / existingKeys > OBSOLETION_FORCE_THRESHOLD

/** The share of the file's keys this import would obsolete, in `0.0..1.0`. */
fun obsoletionRatio(willObsolete: Int, existingKeys: Int): Double =
    if (existingKeys <= 0) 0.0 else willObsolete.toDouble() / existingKeys

/** What an import would do to one key. */
@Serializable
enum class KeyChange {
    /** The key does not exist yet and the file defines it. */
    CREATE,

    /** The key exists and the file gives it a different value, comment, or plurality. */
    UPDATE,

    /** The key exists, the file does not mention it, and obsoletion is enabled. */
    OBSOLETE,

    /**
     * The file defines a key this project does not have, in a target locale. A translation has
     * nowhere to go until the source locale defines the key.
     */
    SKIP,

    /** The file agrees with what is stored. */
    UNCHANGED,
}

/**
 * One key's before and after.
 *
 * **A count is not evidence.** `willUpdate: 412` says nothing about whether a `.stringsdict` survived
 * the round trip — a mangled plural and a corrected typo are both "an update". The per-key diff is
 * what lets someone confirm an import rather than agree to one.
 */
@Serializable
data class KeyDiffDto(
    val key: String,
    val change: KeyChange,
    /** What is stored now. Null when the key does not exist yet. */
    val before: String? = null,
    /** What the file says. Null when the key is being obsoleted. */
    val after: String? = null,
    /** Set when either side is a plural, so the UI can show the categories rather than one string. */
    val beforePlural: Map<String, String>? = null,
    val afterPlural: Map<String, String>? = null,
)

/**
 * What an import would do, agreed before it does it.
 *
 * Import used to be a single call that reported afterwards how many keys it had removed — the wrong
 * order for an operation that can destroy work. The plan is computed, shown, and committed by [id],
 * so the numbers the customer agreed to and the numbers the server acts on are the same numbers.
 */
@Serializable
data class ImportPlanDto(
    val id: String,
    val locale: String,
    val path: String,
    val willCreate: Int = 0,
    val willUpdate: Int = 0,
    val willObsolete: Int = 0,
    val willSkip: Int = 0,
    val willUnchange: Int = 0,
    /**
     * Named, not counted. "This will remove 412 keys" is a number nobody can check; the names are
     * how someone recognises that a whole feature area is about to disappear.
     */
    val obsoleteKeys: List<String> = emptyList(),
    /**
     * True when nothing would be lost — no obsoletions, and every update is recoverable from
     * history. False is not a refusal, it is a different confirmation.
     */
    val reversible: Boolean = true,
    val warnings: List<String> = emptyList(),
    /** Per-key before and after. Capped; see [diffTruncated]. */
    val diff: List<KeyDiffDto> = emptyList(),
    /**
     * True when the diff was cut short. A plan for a 40,000-key file must not be a 40,000-row
     * response, and silently sending the first few hundred without saying so would make the diff a
     * worse lie than the count it replaced.
     */
    val diffTruncated: Boolean = false,
    /** Keys already in the target file, which is what [obsoleteRatio] is measured against. */
    val existingKeys: Int = 0,
    val obsoleteRatio: Double = 0.0,
    /** True when committing needs `force`, because the obsoletion is above the threshold. */
    val requiresForce: Boolean = false,
    val expiresAt: String,
)


/**
 * Importing a translation memory.
 *
 * Not an [ImportRequest]: TMX has no key names and carries every language at once, so it never
 * creates keys and never names a file. It fills the translations of keys that already exist.
 */
@Serializable
data class TmxImportRequest(
    val content: String,
    /** The target locale to fill. Must be a target — a memory cannot write the source. */
    val locale: String,
    /**
     * Which language in the file is the source, when its header does not say.
     *
     * A memory assembled from several projects declares `srclang="*all*"`, which is legal and means
     * the file cannot answer this. Without an override the project's own source locale is assumed.
     */
    val sourceLanguage: String? = null,
    /**
     * Report what would be filled without filling it.
     *
     * The interesting number in a migration — how much of a forty-thousand-string history actually
     * lands — is worth knowing before it does.
     */
    val dryRun: Boolean = false,
)

/**
 * A file format Lingvo can read and write.
 *
 * Derived from the format implementations rather than typed into a list, so it cannot claim a
 * capability the code does not have — the direction of error that makes someone choose the product
 * for something it cannot do.
 */
@Serializable
data class FormatDto(
    val id: String,
    val name: String,
    val extension: String,
    /** Whether a key's description survives a round trip. */
    val supportsComments: Boolean,
    val supportsPlurals: Boolean,
    /** The plural categories this format can write, or null when it can write any of them. */
    val pluralCategories: List<String>? = null,
    /** Example paths that would be detected as this format. Empty when the extension is ambiguous. */
    val detectedFrom: List<String> = emptyList(),
    /** How this format writes a variable, in the syntax a translator will actually see. */
    val placeholderSyntax: String = "",
    /**
     * Whether an export writes keys back in the order they were read.
     *
     * A format that reorders produces a diff touching every line on every sync, which buries the one
     * line that actually changed.
     */
    val preservesKeyOrder: Boolean = true,
    /**
     * What does not survive a round trip.
     *
     * Empty means nothing is lost. **Stated rather than discovered**: people who have been burned by
     * a localization tool ask for the exit before they sign.
     */
    val lostOnExport: List<String> = emptyList(),
)

/**
 * Several files in one request.
 *
 * Existed as a client-side loop before, which meant twelve requests, twelve chances for the network
 * to interrupt half-way, and no single answer to "did my import work".
 */
@Serializable
data class BatchImportRequest(
    val files: List<ImportRequest>,
)

/** Why one file could not be imported, and where the parser gave up. */
@Serializable
data class ImportFileError(
    val code: String,
    val message: String,
    /**
     * The line the parser was reading when it failed, when the parser knows.
     *
     * "Unable to parse import content" sends someone to read a 4,000-line XML file with no idea
     * where to start. A line number turns that into a ten-second fix, and it is information the
     * parser already had and was throwing away.
     */
    val line: Int? = null,
    val column: Int? = null,
)

/** One file's outcome. Exactly one of [result] and [error] is set. */
@Serializable
data class BatchImportFileResult(
    val path: String,
    val locale: String,
    val result: ImportResultDto? = null,
    val error: ImportFileError? = null,
)

/**
 * What a multi-file import did, file by file.
 *
 * **Errors are per file, not per batch.** A malformed `.xliff` among twelve used to fail the whole
 * upload, so eleven good files were rejected because of one bad one — and the customer, who dropped
 * a folder in, had no way to tell which. Each file now imports or fails on its own row, in its own
 * transaction.
 */
@Serializable
data class BatchImportResponse(
    val files: List<BatchImportFileResult> = emptyList(),
) {
    val imported: Int get() = files.count { it.error == null }
    val failed: Int get() = files.count { it.error != null }
}

/** A glossary CSV, as exported from a spreadsheet. */
@Serializable
data class GlossaryImportRequest(
    val content: String,
    /** Report what would happen without writing it. */
    val dryRun: Boolean = false,
)

/** What a glossary import did. */
@Serializable
data class GlossaryImportResultDto(
    val created: Int = 0,
    val updated: Int = 0,
    /**
     * Columns that named something other than one of this project's locales.
     *
     * Named rather than counted, because the usual cause is a near miss — a `de-DE` column against a
     * `de` project — and the fix is obvious once you can see which column was ignored.
     */
    val ignoredLocales: List<String> = emptyList(),
    val warnings: List<String> = emptyList(),
)
